import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import type { PDFFont, PDFPage } from 'pdf-lib';

const MARKDOWN_FILE_PATTERN = /\.(?:md|markdown)$/i;
const PDF_MIME_TYPE = 'application/pdf';
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const PAGE_MARGIN = 48;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const HEADING_SIZES: Record<number, number> = {
  1: 24,
  2: 20,
  3: 18,
  4: 16,
  5: 14,
  6: 13,
};

type FontVariant = 'regular' | 'bold' | 'mono';
type LayoutBlock = TextBlock | DividerBlock | SpacerBlock;

interface TextBlock {
  type: 'text';
  text: string;
  fontVariant: FontVariant;
  fontSize: number;
  lineHeight: number;
  spacingBefore: number;
  spacingAfter: number;
}

interface DividerBlock {
  type: 'divider';
  spacingBefore: number;
  spacingAfter: number;
}

interface SpacerBlock {
  type: 'spacer';
  size: number;
}

interface MarkdownToPdfStreamResult {
  fileName: string;
  mimeType: typeof PDF_MIME_TYPE;
  stream: ReadableStream<Uint8Array>;
}

interface PdfFonts {
  regular: PDFFont;
  bold: PDFFont;
  mono: PDFFont;
}

interface PdfLayoutContext {
  currentPage: PDFPage;
  currentY: number;
  readonly pdfDocument: PDFDocument;
  readonly fonts: PdfFonts;
}

export const getPdfFileName = (markdownFileName: string): string => {
  if (markdownFileName.length === 0) {
    throw new Error('A markdown file name is required.');
  }

  return markdownFileName.replace(MARKDOWN_FILE_PATTERN, '') + '.pdf';
};

export const convertMarkdownFileToPdfStream = async (
  markdownFile: File
): Promise<MarkdownToPdfStreamResult> => {
  validateMarkdownFile(markdownFile);

  const markdownContent = await readMarkdownFile(markdownFile);
  const pdfBytes = await convertMarkdownToPdfBytes(markdownContent, markdownFile.name);

  return {
    fileName: getPdfFileName(markdownFile.name),
    mimeType: PDF_MIME_TYPE,
    stream: createPdfReadableStream(pdfBytes),
  };
};

const validateMarkdownFile = (markdownFile: File): void => {
  if (!MARKDOWN_FILE_PATTERN.test(markdownFile.name)) {
    throw new Error('The provided file must use a .md or .markdown extension.');
  }
};

const readMarkdownFile = async (markdownFile: Blob): Promise<string> => {
  if (typeof markdownFile.text === 'function') {
    return markdownFile.text();
  }

  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onerror = () => {
      reject(fileReader.error ?? new Error('Failed to read the markdown file.'));
    };

    fileReader.onload = () => {
      if (typeof fileReader.result !== 'string') {
        reject(new Error('Failed to read the markdown file as text.'));
        return;
      }

      resolve(fileReader.result);
    };

    fileReader.readAsText(markdownFile);
  });
};

const convertMarkdownToPdfBytes = async (
  markdownContent: string,
  markdownFileName: string
): Promise<Uint8Array> => {
  const pdfDocument = await PDFDocument.create();
  pdfDocument.setTitle(getPdfFileName(markdownFileName));
  pdfDocument.setSubject('Markdown to PDF conversion');
  pdfDocument.setProducer('almarag-com-v2');

  const fonts: PdfFonts = {
    regular: await pdfDocument.embedFont(StandardFonts.Helvetica),
    bold: await pdfDocument.embedFont(StandardFonts.HelveticaBold),
    mono: await pdfDocument.embedFont(StandardFonts.Courier),
  };

  const blocks = createLayoutBlocks(markdownContent);
  const layoutContext = createLayoutContext(pdfDocument, fonts);

  if (blocks.length === 0) {
    drawTextBlock(layoutContext, {
      type: 'text',
      text: ' ',
      fontVariant: 'regular',
      fontSize: 12,
      lineHeight: 16,
      spacingBefore: 0,
      spacingAfter: 0,
    });
  } else {
    blocks.forEach((block) => drawLayoutBlock(layoutContext, block));
  }

  return pdfDocument.save();
};

const createLayoutContext = (pdfDocument: PDFDocument, fonts: PdfFonts): PdfLayoutContext => {
  const firstPage = pdfDocument.addPage([PAGE_WIDTH, PAGE_HEIGHT]);

  return {
    currentPage: firstPage,
    currentY: PAGE_HEIGHT - PAGE_MARGIN,
    pdfDocument,
    fonts,
  };
};

const createLayoutBlocks = (markdownContent: string): LayoutBlock[] => {
  const lines = markdownContent.replace(/\r\n/g, '\n').split('\n');
  const blocks: LayoutBlock[] = [];
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const currentLine = lines[lineIndex];
    const trimmedLine = currentLine.trim();

    if (trimmedLine.length === 0) {
      const lastBlock = blocks[blocks.length - 1];

      if (lastBlock?.type !== 'spacer') {
        blocks.push({
          type: 'spacer',
          size: 6,
        });
      }

      lineIndex += 1;
      continue;
    }

    if (/^```/.test(trimmedLine)) {
      const codeLines: string[] = [];
      lineIndex += 1;

      while (lineIndex < lines.length && !/^```/.test(lines[lineIndex].trim())) {
        codeLines.push(lines[lineIndex]);
        lineIndex += 1;
      }

      if (lineIndex < lines.length) {
        lineIndex += 1;
      }

      blocks.push({
        type: 'text',
        text: codeLines.join('\n'),
        fontVariant: 'mono',
        fontSize: 10,
        lineHeight: 14,
        spacingBefore: 6,
        spacingAfter: 10,
      });
      continue;
    }

    const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const depth = headingMatch[1].length;
      const fontSize = HEADING_SIZES[depth] ?? HEADING_SIZES[6];

      blocks.push({
        type: 'text',
        text: headingMatch[2],
        fontVariant: 'bold',
        fontSize,
        lineHeight: fontSize + 6,
        spacingBefore: depth === 1 ? 0 : 4,
        spacingAfter: 10,
      });
      lineIndex += 1;
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmedLine)) {
      blocks.push({
        type: 'divider',
        spacingBefore: 6,
        spacingAfter: 10,
      });
      lineIndex += 1;
      continue;
    }

    if (/^>\s?/.test(trimmedLine)) {
      const quoteLines: string[] = [];

      while (lineIndex < lines.length && /^>\s?/.test(lines[lineIndex].trim())) {
        quoteLines.push(lines[lineIndex].trim().replace(/^>\s?/, ''));
        lineIndex += 1;
      }

      blocks.push(...createParagraphBlock(`> ${quoteLines.join(' ')}`, 11, 15, 4, 8));
      continue;
    }

    const listMatch = trimmedLine.match(/^((?:[-*+])|(?:\d+\.))\s+(.*)$/);
    if (listMatch) {
      const orderedList = /\d+\./.test(listMatch[1]);
      let listPosition = 0;
      let orderedStart = orderedList ? Number.parseInt(listMatch[1], 10) : 1;

      while (lineIndex < lines.length) {
        const candidateLine = lines[lineIndex].trim();
        const candidateMatch = candidateLine.match(/^((?:[-*+])|(?:\d+\.))\s+(.*)$/);

        if (!candidateMatch) {
          break;
        }

        if (orderedList !== /\d+\./.test(candidateMatch[1])) {
          break;
        }

        const prefix = orderedList ? `${orderedStart + listPosition}.` : '-';
        blocks.push(...createParagraphBlock(`${prefix} ${candidateMatch[2]}`, 12, 16, 2, 4));
        listPosition += 1;
        lineIndex += 1;

        if (orderedList) {
          orderedStart = Number.parseInt(candidateMatch[1], 10);
        }
      }

      continue;
    }

    if (looksLikeTable(trimmedLine)) {
      const tableLines: string[] = [];

      while (lineIndex < lines.length && looksLikeTable(lines[lineIndex].trim())) {
        tableLines.push(lines[lineIndex].trim());
        lineIndex += 1;
      }

      const contentRows = tableLines.filter((line) => !isTableSeparator(line));

      if (contentRows.length > 0) {
        blocks.push(...createParagraphBlock(contentRows[0], 12, 16, 6, 4));

        if (contentRows.length > 1) {
          blocks.push({
            type: 'divider',
            spacingBefore: 0,
            spacingAfter: 6,
          });

          contentRows.slice(1).forEach((row) => {
            blocks.push(...createParagraphBlock(row, 11, 15, 0, 4));
          });
        }
      }

      continue;
    }

    const paragraphLines: string[] = [trimmedLine];
    lineIndex += 1;

    while (lineIndex < lines.length && !startsNewBlock(lines[lineIndex])) {
      paragraphLines.push(lines[lineIndex].trim());
      lineIndex += 1;
    }

    blocks.push(...createParagraphBlock(paragraphLines.join(' ')));
  }

  return blocks;
};

const looksLikeTable = (line: string): boolean =>
  line.includes('|') && line.replace(/\|/g, '').trim().length > 0;

const isTableSeparator = (line: string): boolean => /^\|?[\s:-]+\|[\s|:-]*$/.test(line);

const startsNewBlock = (line: string): boolean => {
  const trimmedLine = line.trim();

  return (
    trimmedLine.length === 0 ||
    /^```/.test(trimmedLine) ||
    /^(#{1,6})\s+/.test(trimmedLine) ||
    /^>\s?/.test(trimmedLine) ||
    /^((?:[-*+])|(?:\d+\.))\s+/.test(trimmedLine) ||
    /^(-{3,}|\*{3,}|_{3,})$/.test(trimmedLine) ||
    looksLikeTable(trimmedLine)
  );
};

const createParagraphBlock = (
  text: string,
  fontSize = 12,
  lineHeight = 16,
  spacingBefore = 0,
  spacingAfter = 8
): TextBlock[] =>
  text.trim().length > 0
    ? [
        {
          type: 'text',
          text,
          fontVariant: 'regular',
          fontSize,
          lineHeight,
          spacingBefore,
          spacingAfter,
        },
      ]
    : [];

const drawLayoutBlock = (layoutContext: PdfLayoutContext, block: LayoutBlock): void => {
  switch (block.type) {
    case 'text':
      drawTextBlock(layoutContext, block);
      return;
    case 'divider':
      drawDividerBlock(layoutContext, block);
      return;
    case 'spacer':
      layoutContext.currentY -= block.size;
      return;
  }
};

const drawTextBlock = (layoutContext: PdfLayoutContext, block: TextBlock): void => {
  const font = layoutContext.fonts[block.fontVariant];
  const lines = wrapText(block.text, font, block.fontSize, CONTENT_WIDTH);
  layoutContext.currentY -= block.spacingBefore;

  lines.forEach((line) => {
    ensureSpace(layoutContext, block.lineHeight);

    layoutContext.currentPage.drawText(normalizePdfText(line), {
      x: PAGE_MARGIN,
      y: layoutContext.currentY,
      size: block.fontSize,
      font,
      color: rgb(0.1, 0.1, 0.1),
    });

    layoutContext.currentY -= block.lineHeight;
  });

  layoutContext.currentY -= block.spacingAfter;
};

const drawDividerBlock = (layoutContext: PdfLayoutContext, block: DividerBlock): void => {
  layoutContext.currentY -= block.spacingBefore;
  ensureSpace(layoutContext, 12);

  layoutContext.currentPage.drawLine({
    start: { x: PAGE_MARGIN, y: layoutContext.currentY },
    end: { x: PAGE_WIDTH - PAGE_MARGIN, y: layoutContext.currentY },
    thickness: 1,
    color: rgb(0.75, 0.75, 0.75),
  });

  layoutContext.currentY -= 12 + block.spacingAfter;
};

const ensureSpace = (layoutContext: PdfLayoutContext, requiredHeight: number): void => {
  if (layoutContext.currentY - requiredHeight >= PAGE_MARGIN) {
    return;
  }

  layoutContext.currentPage = layoutContext.pdfDocument.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  layoutContext.currentY = PAGE_HEIGHT - PAGE_MARGIN;
};

const wrapText = (text: string, font: PDFFont, fontSize: number, maxWidth: number): string[] =>
  normalizePdfText(text)
    .split(/\r?\n/)
    .flatMap((rawLine) => wrapSingleLine(rawLine, font, fontSize, maxWidth));

const wrapSingleLine = (
  rawLine: string,
  font: PDFFont,
  fontSize: number,
  maxWidth: number
): string[] => {
  if (rawLine.trim().length === 0) {
    return [' '];
  }

  const words = rawLine.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine.length > 0 ? `${currentLine} ${word}` : word;

    if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
      currentLine = candidate;
      return;
    }

    if (currentLine.length > 0) {
      lines.push(currentLine);
      currentLine = '';
    }

    if (font.widthOfTextAtSize(word, fontSize) <= maxWidth) {
      currentLine = word;
      return;
    }

    splitLongWord(word, font, fontSize, maxWidth).forEach((segment) => {
      lines.push(segment);
    });
  });

  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [' '];
};

const splitLongWord = (
  word: string,
  font: PDFFont,
  fontSize: number,
  maxWidth: number
): string[] => {
  const segments: string[] = [];
  let currentSegment = '';

  word.split('').forEach((character) => {
    const candidate = currentSegment + character;

    if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
      currentSegment = candidate;
      return;
    }

    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }

    currentSegment = character;
  });

  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments;
};

const normalizePdfText = (text: string): string =>
  text
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ')
    .replace(/[^\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '?');

const createPdfReadableStream = (pdfBytes: Uint8Array): ReadableStream<Uint8Array> =>
  new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(pdfBytes);
      controller.close();
    },
  });

export type { MarkdownToPdfStreamResult };
